import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, vModelText as _vModelText, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers, withKeys as _withKeys } from "vue"

const _hoisted_1 = ["aria-label"]
const _hoisted_2 = ["disabled"]
const _hoisted_3 = ["aria-label"]
const _hoisted_4 = { class: "library-search" }
const _hoisted_5 = ["aria-label", "placeholder"]
const _hoisted_6 = ["aria-label"]
const _hoisted_7 = {
  key: 0,
  class: "library-status",
  role: "status"
}
const _hoisted_8 = {
  key: 1,
  class: "library-status",
  role: "status"
}
const _hoisted_9 = { class: "library-body" }
const _hoisted_10 = ["aria-label"]
const _hoisted_11 = ["onClick"]
const _hoisted_12 = { class: "library-results" }
const _hoisted_13 = {
  key: 0,
  role: "status"
}
const _hoisted_14 = { class: "library-grid" }
const _hoisted_15 = ["aria-label"]
const _hoisted_16 = { key: 0 }
const _hoisted_17 = ["aria-label", "aria-pressed", "title", "onClick"]
const _hoisted_18 = { class: "thumbnail" }
const _hoisted_19 = ["src", "onError"]
const _hoisted_20 = { key: 0 }
const _hoisted_21 = { key: 1 }

import { t } from '../../i18n';
import { computed, onMounted, onBeforeUnmount, ref } from "vue";
import { useI18n } from "vue-i18n";
import EditorIcon from "./EditorIcon.vue";
import { clientImageCatalog, type createImageCatalog, type UIImageAsset } from "./imageAssets";

export default /*@__PURE__*/_defineComponent({
  __name: 'ImageAssetLibrary',
  props: {
    selectedId: {},
    catalog: {},
    loadMetadata: { type: Boolean, default: true }
  },
  emits: ["close", "select"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const { imageAssets, imageAssetById, imageCategories, imageCategoryLabel, imageCatalogLoading, imageCatalogError, loadImageCatalog, useImageCatalogTranslations, loadSpriteMetadata } = props.catalog ?? clientImageCatalog;
const loadTranslations = useImageCatalogTranslations();
const emit = __emit;
const { locale } = useI18n();
const search = ref("");
const category = ref(imageAssetById.get(props.selectedId ?? -1)?.categories[0] ?? "");
const panel = ref<HTMLElement>();
const searchInput = ref<HTMLInputElement>();
const selectionError = ref<string>('');
let selectionVersion = 0;
let previousFocus: HTMLElement | null = null;
const filteredAssets = computed(() => {
  const query = search.value.trim().toLowerCase();
  return imageAssets.filter(asset => (!category.value || asset.categories.includes(category.value)) && (!query || `${asset.id} ${asset.categories.map(key => imageCategoryLabel(key, locale.value)).join(' ')}`.toLowerCase().includes(query)));
});
async function selectAsset(asset: UIImageAsset) {
  const version = ++selectionVersion;
  selectionError.value = "";
  const metadata = props.loadMetadata ? await loadSpriteMetadata(asset) : undefined;
  if (version !== selectionVersion) return;
  if (props.loadMetadata && asset.borderPath && !metadata) selectionError.value = t('clientUIAnimationEditor.ui.imageLibrary.stretchMetadataUnavailable');
  emit("select", asset.id);
}
function clearSelection() { selectionVersion++; selectionError.value = ""; emit("select", null); }
onMounted(() => { previousFocus = document.activeElement as HTMLElement; searchInput.value?.focus(); void loadTranslations(); void loadImageCatalog(false, locale.value); });
onBeforeUnmount(() => { selectionVersion++; if (previousFocus?.isConnected) previousFocus.focus(); });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", {
    ref_key: "panel",
    ref: panel,
    class: "image-library",
    role: "dialog",
    "aria-label": _unref(t)('clientUIAnimationEditor.ui.imageLibrary.title'),
    tabindex: "-1",
    onKeydown: _cache[6] || (_cache[6] = _withKeys(_withModifiers(($event: any) => (_ctx.$emit('close')), ["stop","prevent"]), ["esc"])),
    onPointerdown: _cache[7] || (_cache[7] = _withModifiers(() => {}, ["stop"]))
  }, [
    _createElementVNode("header", null, [
      _createElementVNode("strong", null, _toDisplayString(_unref(t)('clientUIAnimationEditor.ui.imageLibrary.title')), 1),
      _createElementVNode("span", null, _toDisplayString(_unref(t)('clientUIAnimationEditor.ui.common.itemCount', { count: filteredAssets.value.length })), 1),
      _createElementVNode("button", {
        class: "refresh-library",
        disabled: _unref(imageCatalogLoading),
        onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(loadImageCatalog)(true, _unref(locale))))
      }, _toDisplayString(_unref(t)('clientUIAnimationEditor.ui.actions.refresh')), 9, _hoisted_2),
      _createElementVNode("button", {
        "aria-label": _unref(t)('clientUIAnimationEditor.ui.images.closeLibrary'),
        onClick: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('close')))
      }, "×", 8, _hoisted_3)
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createVNode(EditorIcon, {
        name: "search",
        size: 18
      }),
      _withDirectives(_createElementVNode("input", {
        ref_key: "searchInput",
        ref: searchInput,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((search).value = $event)),
        "aria-label": _unref(t)('clientUIAnimationEditor.ui.images.searchResourceLabel'),
        placeholder: _unref(t)('clientUIAnimationEditor.ui.imageLibrary.searchPlaceholder')
      }, null, 8, _hoisted_5), [
        [_vModelText, search.value]
      ]),
      (search.value)
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            "aria-label": _unref(t)('clientUIAnimationEditor.ui.imageLibrary.clearSearch'),
            onClick: _cache[3] || (_cache[3] = ($event: any) => (search.value = ''))
          }, "×", 8, _hoisted_6))
        : _createCommentVNode("", true)
    ]),
    (_unref(imageCatalogError))
      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
          _createTextVNode(_toDisplayString(_unref(imageCatalogError)) + " ", 1),
          _createElementVNode("button", {
            onClick: _cache[4] || (_cache[4] = ($event: any) => (_unref(loadImageCatalog)(true, _unref(locale))))
          }, _toDisplayString(_unref(t)('clientUIAnimationEditor.ui.common.retry')), 1)
        ]))
      : _createCommentVNode("", true),
    (selectionError.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(selectionError.value), 1))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_9, [
      _createElementVNode("nav", {
        "aria-label": _unref(t)('clientUIAnimationEditor.ui.imageLibrary.categories.label')
      }, [
        _createElementVNode("button", {
          class: _normalizeClass({ active: !category.value }),
          onClick: _cache[5] || (_cache[5] = ($event: any) => (category.value = ''))
        }, _toDisplayString(_unref(t)('clientUIAnimationEditor.ui.imageLibrary.allImages')), 3),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(imageCategories), (key) => {
          return (_openBlock(), _createElementBlock("button", {
            key: key,
            class: _normalizeClass({ active: category.value === key }),
            onClick: ($event: any) => (category.value = key)
          }, _toDisplayString(_unref(imageCategoryLabel)(key, _unref(locale))), 11, _hoisted_11))
        }), 128))
      ], 8, _hoisted_10),
      _createElementVNode("div", _hoisted_12, [
        (_unref(imageCatalogLoading))
          ? (_openBlock(), _createElementBlock("p", _hoisted_13, _toDisplayString(_unref(t)('clientUIAnimationEditor.ui.images.loadingLibrary')), 1))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_14, [
          _createElementVNode("button", {
            class: _normalizeClass(["asset-tile empty-asset", { selected: __props.selectedId === null }]),
            "aria-label": _unref(t)('clientUIAnimationEditor.ui.images.none'),
            onClick: clearSelection
          }, [
            _cache[8] || (_cache[8] = _createElementVNode("span", { class: "thumbnail" }, null, -1)),
            _createElementVNode("b", null, _toDisplayString(_unref(t)('clientUIAnimationEditor.ui.imageLibrary.noImage')), 1),
            (__props.selectedId === null)
              ? (_openBlock(), _createElementBlock("i", _hoisted_16, "✓"))
              : _createCommentVNode("", true)
          ], 10, _hoisted_15),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredAssets.value, (asset) => {
            return (_openBlock(), _createElementBlock("button", {
              key: asset.id,
              class: _normalizeClass(["asset-tile", { selected: __props.selectedId === asset.id }]),
              "aria-label": _unref(t)('clientUIAnimationEditor.ui.imageLibrary.selectImageLabel', { value: asset.id }),
              "aria-pressed": __props.selectedId === asset.id,
              title: `${asset.categories.map(key => _unref(imageCategoryLabel)(key, _unref(locale))).join(' / ')} · ${asset.id}`,
              onClick: ($event: any) => (selectAsset(asset))
            }, [
              _createElementVNode("span", _hoisted_18, [
                (asset.src && !asset.missing)
                  ? (_openBlock(), _createElementBlock("img", {
                      key: 0,
                      src: asset.src,
                      alt: "",
                      loading: "lazy",
                      onError: ($event: any) => (asset.missing = true)
                    }, null, 40, _hoisted_19))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("b", null, _toDisplayString(asset.id), 1),
              (__props.selectedId === asset.id)
                ? (_openBlock(), _createElementBlock("i", _hoisted_20, "✓"))
                : _createCommentVNode("", true)
            ], 10, _hoisted_17))
          }), 128))
        ]),
        (!_unref(imageCatalogLoading) && !filteredAssets.value.length)
          ? (_openBlock(), _createElementBlock("p", _hoisted_21, _toDisplayString(_unref(t)('clientUIAnimationEditor.ui.images.noMatches')), 1))
          : _createCommentVNode("", true)
      ])
    ])
  ], 40, _hoisted_1))
}
}

})